'use client';

import { ChartConfig, ChartPreset, ChartType } from '@/types/chart-registry';
import { Button } from '@/components/ui/button';
import * as lucide from 'lucide-react'
import { DropdownMenu, DropdownMenuItem, DropdownMenuContent, DropdownMenuGroup, DropdownMenuLabel, DropdownMenuTrigger, DropdownMenuSub, DropdownMenuSubTrigger, DropdownMenuSubContent, DropdownMenuCheckboxItem, DropdownMenuSeparator  } from './ui/dropdown-menu';
import { ForwardRefExoticComponent, useMemo } from 'react';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from './ui/tooltip';
import { ContextMenu, ContextMenuTrigger, ContextMenuContent, ContextMenuItem } from './ui/context-menu';

interface ChartSelectorProps {
  charts: ChartConfig[];
  presets: ChartPreset[];
  selectedCharts: Set<number>;
  onToggleChart: (chartId: number) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  onApplyPreset: (chartIds: number[]) => void;
  onJumpTo: (chartId: number) => void;
}

const ICONS: Record<ChartType, ForwardRefExoticComponent<any>> = {
  'area': lucide.AreaChart,
  'line': lucide.LineChart,
  'bar': lucide.BarChart,
  'mixed': lucide.ChartSpline,
  'sankey': lucide.Cable,
} as const

export function ChartSelector({charts, selectedCharts, onToggleChart, onSelectAll, onDeselectAll, onApplyPreset, onJumpTo}: ChartSelectorProps) {
  const chartGroups: Record<string, ChartConfig[]> = useMemo(() => {
    if (!charts) return {}
    return charts.reduce((acc, c) => {
        if (!acc[c.category || 'None']) {
            acc[c.category || 'None'] = []
        }
        acc[c.category || 'None'].push(c)
        return acc
    }, {} as Record<string, ChartConfig[]>)
  }, [charts])

  return (
    <TooltipProvider delayDuration={50}>
        <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
                <Button variant="outline">Select queries</Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className='w-100' align='start'>
                <DropdownMenuItem disabled className='lg:text-center'>
                    {selectedCharts.size} / {charts.length} selected
                </DropdownMenuItem>
                <DropdownMenuItem disabled className="hidden lg:block">
                    Right-click query to jump to it
                </DropdownMenuItem>
                <DropdownMenuItem disabled className="block lg:hidden">
                    Tap and hold to jump to a query
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuGroup>
                    <DropdownMenuItem onClick={onSelectAll}>Select All</DropdownMenuItem>
                </DropdownMenuGroup>
                <DropdownMenuGroup>
                    <DropdownMenuItem onClick={onDeselectAll}>Deselect All</DropdownMenuItem>
                </DropdownMenuGroup>
                <DropdownMenuSeparator />
                {Object.entries(chartGroups).map(([group, charts]) => 
                    <DropdownMenuSub key={group}>
                        <ContextMenu>
                            <ContextMenuTrigger asChild>
                                <DropdownMenuSubTrigger>{group}</DropdownMenuSubTrigger>
                            </ContextMenuTrigger>
                            <ContextMenuContent>
                                <ContextMenuItem onClick={() => {
                                    charts
                                        .filter(c => !selectedCharts.has(c.id))
                                        .map(c => c.id)
                                        .forEach(c => onToggleChart(c))
                                }}>
                                    Select All
                                </ContextMenuItem>
                                <ContextMenuItem onClick={() => {
                                    charts
                                        .filter(c => selectedCharts.has(c.id))
                                        .map(c => c.id)
                                        .forEach(c => onToggleChart(c))
                                }}>
                                    Deselect All
                                </ContextMenuItem>
                            </ContextMenuContent>
                        </ContextMenu>
                        <DropdownMenuSubContent>
                            {charts?.map(c => {
                                const Icon = ICONS[c.type]
                                return <Tooltip key={c.id}>
                                    <ContextMenu>
                                    <ContextMenuTrigger asChild>
                                    <TooltipTrigger asChild>
                                    <DropdownMenuCheckboxItem
                                        checked={selectedCharts.has(c.id)}
                                        onClick={
                                            (e) => {
                                                e.preventDefault()
                                                onToggleChart(c.id)
                                            }
                                        }
                                        className='truncate'
                                    >
                                        <Icon size="16" className="mr-2" /> {c.name}
                                    </DropdownMenuCheckboxItem>
                                    </TooltipTrigger>
                                    </ContextMenuTrigger>
                                <TooltipContent side="right" sideOffset={6}>
                                    {c.description}
                                </TooltipContent>
                                <ContextMenuContent>
                                    <ContextMenuItem onClick={() => {
                                        onJumpTo(c.id)
                                    }}>Jump to...</ContextMenuItem>
                                </ContextMenuContent>
                                </ContextMenu>
                                </Tooltip>  
                            })}
                        </DropdownMenuSubContent>
                    </DropdownMenuSub>
                )}
            </DropdownMenuContent>
        </DropdownMenu>
    </TooltipProvider>
  )
}
